import React from "react";
import PropTypes from "prop-types";
import { Card, Box, Typography, Grid, Stack } from "@mui/material";
import {
  CardGiftcardOutlined as GiftCardIcon,
  VolunteerActivismOutlined as DonationIcon,
  ShoppingCartOutlined as SaleIcon,
  MonetizationOnOutlined as TokenIcon,
} from "@mui/icons-material";
import { colors, borderRadiusPx, customShadows } from "../../../../../theme";
import { DEFAULT_TOKEN_SYMBOL } from "../../../../../static/Constants";

const GiftCardsHero = ({
  totalCardsCount = 0,
  donationCount = 0,
  saleCount = 0,
  totalTokenValue = "0",
  tokenTicker = DEFAULT_TOKEN_SYMBOL,
}) => {
  return (
    <Card
      sx={{
        p: { xs: 3, sm: 4 },
        borderRadius: borderRadiusPx.xl,
        backgroundColor: colors.dashboard.cardBg,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.cardFlat,
        position: "relative",
        overflow: "hidden",
        mb: 4,
      }}
    >
      {/* Top Gradient Accent Stripe */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: colors.gradients.primary,
        }}
      />

      <Grid container spacing={3.5} alignItems="center">
        {/* Left Section: Title & Subtitle */}
        <Grid item xs={12} lg={6}>
          <Stack spacing={1.5}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: borderRadiusPx.md,
                  bgcolor: colors.blue[50],
                  color: colors.primary.main,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <GiftCardIcon sx={{ fontSize: 20 }} />
              </Box>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 900,
                  color: colors.slate[900],
                  letterSpacing: "-0.5px",
                  fontSize: { xs: "1.75rem", sm: "2.25rem", md: "2.5rem" },
                  textAlign: "left",
                }}
              >
                Digital Gift Cards
              </Typography>
            </Box>

            <Typography
              variant="body1"
              sx={{
                color: colors.slate[600],
                fontSize: { xs: "0.925rem", sm: "1rem" },
                maxWidth: 580,
                lineHeight: 1.6,
                textAlign: "left",
              }}
            >
              Discover and claim instant Web3 gift cards on-chain. Redeem free
              promotional token giveaways or purchase discounted betting credits
              minted by verified creators.
            </Typography>
          </Stack>
        </Grid>

        {/* Right Section: Metric Stats Grid */}
        <Grid item xs={12} lg={6}>
          <Grid container spacing={2}>
            {/* Stat 1: Total Active Cards */}
            <Grid item xs={6} sm={3} lg={6}>
              <Box
                className="giftcard-metric-card"
                sx={{
                  p: 2,
                  borderRadius: borderRadiusPx.lg,
                  bgcolor: colors.slate[50],
                  border: `1px solid ${colors.slate[200]}`,
                  height: "100%",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: borderRadiusPx.md,
                      bgcolor: colors.blue[50],
                      color: colors.primary.main,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <GiftCardIcon sx={{ fontSize: 16 }} />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.slate[600],
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.7rem",
                    }}
                  >
                    Active Cards
                  </Typography>
                </Stack>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 900,
                    color: colors.slate[900],
                    fontSize: "1.45rem",
                  }}
                >
                  {totalCardsCount}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.emerald[600], fontWeight: 600 }}>
                  Ready to claim
                </Typography>
              </Box>
            </Grid>

            {/* Stat 2: Free Giveaways / Donations */}
            <Grid item xs={6} sm={3} lg={6}>
              <Box
                className="giftcard-metric-card"
                sx={{
                  p: 2,
                  borderRadius: borderRadiusPx.lg,
                  bgcolor: colors.amber[50],
                  border: `1px solid ${colors.amber[200]}`,
                  height: "100%",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: borderRadiusPx.md,
                      bgcolor: colors.amber[100],
                      color: colors.amber[800],
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <DonationIcon size={14} />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.amber[800],
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.7rem",
                    }}
                  >
                    Free Vouchers
                  </Typography>
                </Stack>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 900,
                    color: colors.amber[900],
                    fontSize: "1.45rem",
                  }}
                >
                  {donationCount}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.amber[700], fontWeight: 600 }}>
                  100% Free Claim
                </Typography>
              </Box>
            </Grid>

            {/* Stat 3: Total Token Value Pool */}
            <Grid item xs={6} sm={3} lg={6}>
              <Box
                className="giftcard-metric-card"
                sx={{
                  p: 2,
                  borderRadius: borderRadiusPx.lg,
                  bgcolor: colors.blue[50],
                  border: `1px solid ${colors.blue[200]}`,
                  height: "100%",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: borderRadiusPx.md,
                      bgcolor: colors.primary.main,
                      color: colors.neutral.white,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <TokenIcon sx={{ fontSize: 16 }} />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.slate[600],
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.7rem",
                    }}
                  >
                    Total Value Pool
                  </Typography>
                </Stack>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 900,
                    color: colors.primary.dark,
                    fontSize: "1.45rem",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {totalTokenValue} {tokenTicker}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.primary.main, fontWeight: 600 }}>
                  On-chain liquidity
                </Typography>
              </Box>
            </Grid>

            {/* Stat 4: For Sale Cards */}
            <Grid item xs={6} sm={3} lg={6}>
              <Box
                className="giftcard-metric-card"
                sx={{
                  p: 2,
                  borderRadius: borderRadiusPx.lg,
                  bgcolor: colors.emerald[50],
                  border: `1px solid ${colors.emerald[200]}`,
                  height: "100%",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: borderRadiusPx.md,
                      bgcolor: colors.emerald[100],
                      color: colors.emerald[800],
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <SaleIcon size={14} />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.emerald[800],
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.7rem",
                    }}
                  >
                    Direct Sale
                  </Typography>
                </Stack>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 900,
                    color: colors.emerald[900],
                    fontSize: "1.45rem",
                  }}
                >
                  {saleCount}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.emerald[700], fontWeight: 600 }}>
                  Fixed price cards
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Card>
  );
};

GiftCardsHero.propTypes = {
  totalCardsCount: PropTypes.number,
  donationCount: PropTypes.number,
  saleCount: PropTypes.number,
  totalTokenValue: PropTypes.string,
  tokenTicker: PropTypes.string,
};

export default GiftCardsHero;
